import { BaseModel } from "../../../../core/util/BaseModel";
import { action, makeObservable, observable } from "mobx";
import { ButtonTypes } from "../../../../components/ui/Button";
import { Icon, IconSymbols } from "../../../../components/ui/Icon";
import { Panel } from "../../../../components/ui/Panel";
import ProgressIndicatorModel, {
  ProgressIndicatorModel as IProgressIndicatorModel
} from "../../../../components/widgets/ProgressIndicator/ProgressIndicator_model";
import I18n from "../../../../core/localization/I18n";
import ToasterService, { IToasterService } from "../../../../core/toaster/ToasterService";
import { TOASTER_TOAST_TIME } from "../../../../core/toaster/Toaster_model";
import { UiActionRenderers } from "../../../../core/uiAction/IUiAction";
import { Enums } from "../../../../enums";
import OrganisationsApi, {
  OrganisationsApi as IOrganisationsApi
} from "../../../../services/api/v2/organisations/Organisations.api";
import ProjectsApi, { ProjectsApi as IProjectsApi } from "../../../../services/api/v2/projects/Projects.api";
import { ILocalStorageService } from "../../../../services/local/localStorageService/ILocalStorageService";
import LocalStorageService from "../../../../services/local/localStorageService/LocalStorageService";
import { getProjectFormFields } from "../../forms/project/ProjectFormSection_data";
import { SingleFormModel } from "../../forms/singleFormModel/SingleForm_model";
import { IModalContextModel } from "../../../../core/modalZ/context/IModalContext";
import ModalContext from "../../../../core/modalZ/context/ModalContext";
import Pages from "../../../../routes/InsightRoutes";

export class ProjectSettingsModel extends BaseModel {
  formModel: SingleFormModel;
  modalService: IModalContextModel;
  projectsProvider: IProjectsApi;
  organisationsProvider: IOrganisationsApi;
  localStorageService: ILocalStorageService;
  toasterService: IToasterService;
  orgId: number;
  teamUsers;
  httpProgress: IProgressIndicatorModel;
  @observable projectId: number;
  @observable isLoading: boolean = true;
  @observable.ref project: FP.Entities.IProject;
  user: FP.Entities.IUser;
  refreshClaims: () => void;

  constructor(projectId: number, user: FP.Entities.IUser, refreshClaims: () => void) {
    super();
    makeObservable(this);
    this.user = user;
    this.modalService = ModalContext;
    this.httpProgress = ProgressIndicatorModel;
    this.organisationsProvider = OrganisationsApi;
    this.projectsProvider = ProjectsApi;
    this.refreshClaims = refreshClaims;
    this.projectId = projectId;
    this.localStorageService = LocalStorageService;
    this.toasterService = ToasterService;
    this.orgId = parseInt(this.localStorageService.get(Enums.LocalCookies.ORGANISATION_ID), 10);
    this.formModel = new SingleFormModel();
  }

  onMount = async () => {
    await this.loadProject();
  };
  @action
  setProject = (project: FP.Entities.IProject) => {
    this.project = project;

    this.setForms();
    this.isLoading = false;
  };

  @action
  loadProject = async () => {
    this.isLoading = true;
    let res = await this.projectsProvider.getById(this.orgId, this.projectId);
    if (!res || res.isError) {
      return;
    }

    this.setProject(res.payload);
  };

  @action
  setForms = () => {
    this.formModel.formFields = getProjectFormFields(this.orgId, this.organisationsProvider, this.project);

    this.formModel.actions = [
      {
        id: "delete",
        label: I18n.t("phrases.deleteProject"),
        rendersIn: UiActionRenderers.BUTTON,
        componentProps: {
          type: ButtonTypes.OUTLINE_PRIMARY
        },
        onAction: this.showConfirmDeleteModal
      },
      {
        id: "cancel",
        label: I18n.t("phrases.cancel"),
        rendersIn: UiActionRenderers.LINK_BUTTON,
        componentProps: {
          type: ButtonTypes.LINK,
          href: `${Pages.projects.index.generateLink(this.orgId, this.project.id)}`,
          className: "ml-auto"
        }
      },
      {
        id: "SaveProjectButton",
        label: I18n.t("phrases.save"),
        onAction: async () => {
          const formRes = await this.formModel.submit();

          if (!formRes) return;

          this.httpProgress.showOverlay();
          const res = await this.projectsProvider.update(this.orgId, this.project.id, formRes as FP.Entities.IProject);
          this.refreshClaims();
          this.httpProgress.hideOverlay();

          if (!res || res.isError) {
            return;
          }
        },
        rendersIn: UiActionRenderers.BUTTON,
        componentProps: {
          type: ButtonTypes.PRIMARY,
          className: "ml-2"
        }
      }
    ];
  };

  showConfirmDeleteModal = () => {
    this.modalService.show({
      title: <h1 className="my-4">{I18n.t("phrases.deleteProject")}</h1>,
      content: (
        <div className="container-fluid">
          <div className="row mb-3">
            <div className="col-12">
              <Icon symbol={IconSymbols.AlertCircle} className="mr-2" />
              {I18n.t("warnings.deleteProject")}
            </div>
          </div>
          <div className="row mb-3">
            <div className="col-12">{I18n.t("phrases.confirmDelete", { name: this.project.name })}</div>
          </div>
        </div>
      ),
      showClose: true,
      componentProps: {
        wrapWidth: "small",
        spacing: "small",
        position: "middle",
        panelProps: {
          background: Panel.PanelBackgrounds.BG_WHITE
        }
      },
      actions: [
        {
          id: "delete",
          label: I18n.t("phrases.delete"),
          rendersIn: UiActionRenderers.BUTTON,
          componentProps: {
            type: ButtonTypes.DANGER,
            className: "float-right ml-2"
          },
          onAction: this.deleteProject
        },
        {
          id: "cancel",
          label: I18n.t("phrases.cancel"),
          rendersIn: UiActionRenderers.BUTTON,
          componentProps: {
            type: ButtonTypes.LINK,
            className: "float-right"
          },
          onAction: this.modalService.hide
        }
      ]
    });
  };

  deleteProject = async () => {
    this.modalService.hide();

    this.httpProgress.showOverlay();
    const res = await this.projectsProvider.remove(this.orgId, this.project.id);
    this.refreshClaims();
    this.httpProgress.hideOverlay();

    if (!res || res.isError) {
      this.toasterService.showErrorToast(TOASTER_TOAST_TIME.SLOW).setContent(<p>{I18n.t("errors.deleteProject")}</p>);
      return;
    }

    // appHistory.push(`/organisations/${this.orgId}`);
  };
}
